import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Tv, Layers, Globe, Eye, Users, Crown, Shield, FileText,
  BarChart3, AlertCircle, MessageSquare, DollarSign,
  Plus, Settings, ScrollText, Inbox, ArrowRight, Clock,
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import { useSubmissionsCounter } from "@/components/AdminLayout";

const ACTION_LABELS = {
  role_assigned: "Role Assigned",
  moderator_assigned: "Moderator Assigned",
  moderator_revoked: "Moderator Revoked",
  user_suspended: "User Suspended",
  user_reactivated: "User Reactivated",
  permission_changed: "Permissions Changed",
  setting_changed: "Setting Changed",
  report_resolved: "Report Resolved",
  report_dismissed: "Report Dismissed",
  case_escalated: "Case Escalated",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [pstats, setPstats] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const { counter } = useSubmissionsCounter();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/admin/dashboard-stats`, { withCredentials: true });
        setStats(res.data);
      } catch { }
      setLoading(false);
    };
    fetchStats();
    axios.get(`${API}/admin/channels/health/summary`, { withCredentials: true }).then((r) => setHealth(r.data)).catch(() => {});
    axios.get(`${API}/admin/users/stats`, { withCredentials: true }).then((r) => setPstats(r.data)).catch(() => {});
    axios.get(`${API}/admin/users/premium/expiring`, { params: { days: 7 }, withCredentials: true }).then((r) => setExpiring(r.data)).catch(() => {});
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-24 bg-slate-800 rounded-xl" /><div className="h-24 bg-slate-800 rounded-xl" /></div>;

  const mainCards = [
    { label: "Live Users", value: stats?.live_users || 0, icon: Users, color: "text-green-400", bg: "bg-green-500/10", pulse: true },
    { label: "Total Channels", value: stats?.total_channels || 0, icon: Tv, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Active Channels", value: stats?.active_channels || 0, icon: Tv, color: "text-cyan-400", bg: "bg-cyan-500/10" },
    { label: "Categories", value: stats?.total_categories || 0, icon: Layers, color: "text-purple-400", bg: "bg-purple-500/10" },
    { label: "Countries", value: stats?.total_countries || 0, icon: Globe, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  ];

  const secondaryCards = [
    { label: "Total Users", value: stats?.total_users || 0, icon: Users, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Premium Members", value: pstats?.premium_active ?? stats?.active_members ?? 0, icon: Crown, color: "text-amber-400", bg: "bg-amber-500/10" },
    { label: "Moderators", value: stats?.total_moderators || 0, icon: Shield, color: "text-green-400", bg: "bg-green-500/10" },
    { label: "Total Views", value: stats?.total_views || 0, icon: Eye, color: "text-orange-400", bg: "bg-orange-500/10" },
    { label: "Open Reports", value: stats?.open_reports || 0, icon: AlertCircle, color: "text-red-400", bg: "bg-red-500/10" },
  ];

  const quickActions = [
    { label: "Add Channel", icon: Tv, path: "/admin/channels", color: "bg-blue-600 hover:bg-blue-500" },
    { label: "Add Category", icon: Layers, path: "/admin/categories", color: "bg-purple-600 hover:bg-purple-500" },
    { label: "Manage Users", icon: Users, path: "/admin/users", color: "bg-emerald-600 hover:bg-emerald-500" },
    { label: "Audit Log", icon: ScrollText, path: "/admin/audit-log", color: "bg-amber-600 hover:bg-amber-500" },
    { label: "Settings", icon: Settings, path: "/admin/settings", color: "bg-slate-600 hover:bg-slate-500" },
  ];

  return (
    <>
      {/* New Channel Submissions banner */}
      {counter.new > 0 && (
        <Link to="/admin/channel-submissions" data-testid="dashboard-submissions-badge"
          role="status" aria-label={`${counter.new} new channel submissions awaiting review`}
          className="flex items-center gap-3 mb-6 p-4 rounded-xl border border-[#FFC928]/30 bg-[#FFC928]/10 hover:bg-[#FFC928]/15 transition-colors group">
          <div className="p-2 rounded-lg bg-[#FFC928]/20"><Inbox className="text-[#FFC928]" size={18} /></div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-white">
              {counter.new > 99 ? "99+" : counter.new} new channel {counter.new === 1 ? "submission" : "submissions"} awaiting review
            </div>
            {counter.my_under_review > 0 && (
              <div className="text-xs text-slate-300 mt-0.5">{counter.my_under_review} assigned to you (under review)</div>
            )}
          </div>
          <ArrowRight size={18} className="text-[#FFC928] group-hover:translate-x-0.5 transition-transform" />
        </Link>
      )}

      {/* Channel Health widget */}
      {health && (
        <Link to="/admin/channels" data-testid="dashboard-health-widget"
          className="block mb-6 p-5 rounded-2xl border border-slate-800 bg-slate-900 hover:border-slate-700 transition-colors">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-lg bg-blue-500/10"><Tv className="text-blue-400" size={18} /></div>
            <h3 className="text-sm font-bold text-white">Channel Health</h3>
            <span className="ml-auto text-xs text-slate-500">{health.total} channels</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Online", health.online, "text-green-400"], ["Offline", health.offline, "text-red-400"],
              ["Warnings", health.warnings, "text-yellow-400"], ["Never Tested", health.never_tested, "text-slate-400"]].map(([l, v, c]) => (
              <div key={l} className="bg-slate-950 rounded-xl p-3" data-testid={`health-stat-${l.toLowerCase().replace(" ", "-")}`}>
                <div className={cn("text-2xl font-bold", c)}>{v}</div>
                <div className="text-xs text-slate-500">{l}</div>
              </div>
            ))}
          </div>
        </Link>
      )}

      {/* Expiring Complimentary Premium widget */}
      {expiring && expiring.count > 0 && (
        <Link to="/admin/users" data-testid="dashboard-expiring-premium"
          className="block mb-6 p-5 rounded-2xl border border-amber-500/30 bg-amber-500/5 hover:border-amber-500/50 transition-colors">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-2 rounded-lg bg-amber-500/10"><Clock className="text-amber-400" size={18} /></div>
            <h3 className="text-sm font-bold text-white">Complimentary Premium Expiring Soon</h3>
            <span className="ml-auto text-xs text-amber-400 font-semibold">{expiring.count} in 7 days</span>
          </div>
          <div className="space-y-1.5">
            {expiring.expiring.slice(0, 5).map((u) => (
              <div key={u.id} className="flex items-center justify-between text-sm" data-testid={`dash-expiring-${u.id}`}>
                <span className="text-slate-300 truncate">{u.name || u.email}</span>
                <span className={cn("ml-3 flex-shrink-0 text-xs font-semibold", (u.days_left ?? 99) <= 3 ? "text-red-400" : "text-amber-400")}>
                  {u.days_left === 0 ? "Today" : `${u.days_left}d left`}
                </span>
              </div>
            ))}
            {expiring.count > 5 && <div className="text-xs text-slate-500 pt-1">+{expiring.count - 5} more…</div>}
          </div>
        </Link>
      )}

      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-testid="stats-grid">
        {mainCards.map((stat, idx) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.06 }}
            className="bg-slate-900 p-4 rounded-xl border border-slate-800" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <div className={cn("p-2 rounded-lg", stat.bg)}><stat.icon className={stat.color} size={18} /></div>
              {stat.pulse && (
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                </span>
              )}
            </div>
            <div className="text-2xl font-bold text-white">{stat.value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {secondaryCards.map((stat, idx) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + idx * 0.06 }}
            className="bg-slate-900/50 p-3.5 rounded-xl border border-slate-800/60">
            <div className="flex items-center gap-2.5">
              <div className={cn("p-1.5 rounded-lg", stat.bg)}><stat.icon className={stat.color} size={14} /></div>
              <div>
                <div className="text-lg font-bold text-white">{stat.value}</div>
                <div className="text-[11px] text-slate-500">{stat.label}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions + Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden" data-testid="quick-actions">
          <div className="p-4 border-b border-slate-800">
            <h3 className="font-semibold text-sm text-white flex items-center gap-2">
              <Plus size={16} className="text-slate-500" /> Quick Actions
            </h3>
          </div>
          <div className="p-3 space-y-2">
            {quickActions.map(action => (
              <Link key={action.label} to={action.path}
                className={cn("flex items-center gap-3 px-3 py-2.5 rounded-lg text-white text-sm font-medium transition-colors", action.color)}
                data-testid={`quick-${action.label.toLowerCase().replace(/\s/g, '-')}`}>
                <action.icon size={16} /> {action.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity Feed */}
        <div className="lg:col-span-2 bg-slate-900 rounded-xl border border-slate-800 overflow-hidden" data-testid="activity-feed">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h3 className="font-semibold text-sm text-white flex items-center gap-2">
              <BarChart3 size={16} className="text-slate-500" /> Recent Activity
            </h3>
            <Link to="/admin/audit-log" className="text-xs text-blue-400 hover:text-blue-300">View All</Link>
          </div>
          <div className="divide-y divide-slate-800/50">
            {stats?.recent_activity?.length > 0 ? stats.recent_activity.slice(0, 10).map((log, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-slate-800/20">
                <div>
                  <span className="text-sm text-white font-medium">{ACTION_LABELS[log.action] || log.action.replace(/_/g, ' ')}</span>
                  {log.target_name && <span className="text-xs text-slate-500 ml-2">on {log.target_name}</span>}
                  <div className="text-[10px] text-slate-600 mt-0.5">by {log.actor_name}</div>
                </div>
                <span className="text-[10px] text-slate-600 flex-shrink-0">{new Date(log.created_at).toLocaleString()}</span>
              </div>
            )) : (
              <div className="p-8 text-center text-sm text-slate-500">
                No activity yet. Actions will appear here as you manage the platform.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
